import React from "react";
import { Alert, Checkbox, Heading, Stack } from "@origin-digital/ods-core";
import { ServiceTypeEnum } from "src/components/Premises";
import { CTReadOnlyServiceSection } from "../CTReadOnlyServiceSection";
import FormFieldLayout from "../../FormComponents/FormFieldLayout";
import DatePickerField from "../../FormComponents/DatePickerField";
import { isUnavailableDateForFuture } from "../../helper";
import { usePermissions } from "src/hooks/usePermissions";
import {
  handoverTypeToLabelMap,
  handoverTypeToLabelMapPassive,
} from "../helper";
import { useCloseTransferFormProps } from "./useCloseTransferFormProps";

type Props = {
  onUpdate: (
    serviceType: ServiceTypeEnum,
    actionType: "cancel" | "action"
  ) => Promise<void>;
};

export const CloseTransferFormLegacyServiceSection: React.FC<Props> = ({
  onUpdate,
}) => {
  const { hasViewAllRequestsPermissions } = usePermissions();

  const { formikContext, formBehavior } = useCloseTransferFormProps();
  const { values, setFieldValue, errors } = formikContext;
  const { closeTransfer } = values;
  const {
    hasElectricitySection,
    isElectricityEnable,
    isElectricityReadOnly,
    hasGasSection,
    isGasEnable,
    isGasReadOnly,
  } = formBehavior;

  const onChangeServiceType = (
    event: React.ChangeEvent<HTMLInputElement>,
    serviceType: ServiceTypeEnum
  ) => {
    if (event.target.checked) {
      setFieldValue("closeTransfer.serviceTypes", [
        ...values.closeTransfer.serviceTypes,
        serviceType,
      ]);
    } else {
      setFieldValue(
        "closeTransfer.serviceTypes",
        values.closeTransfer.serviceTypes.filter(
          (service) => service !== serviceType
        )
      );
    }
  };

  const serviceTypeLabel = handoverTypeToLabelMap[closeTransfer.handoverType];
  const serviceTypeLabelPassive =
    handoverTypeToLabelMapPassive[closeTransfer.handoverType];

  const isOnlyOneServiceType = hasElectricitySection !== hasGasSection;

  return (
    <Stack
      space={{
        xs: "xlarge",
        md: "xxlarge",
      }}
    >
      {/* Service */}
      {hasElectricitySection && (
        <Stack
          space={{
            xs: "large",
            md: "xlarge",
          }}
        >
          {/* Close */}
          {/* if there is only one service type, the checkbox will be checked by default */}
          {isElectricityReadOnly ? (
            <CTReadOnlyServiceSection
              handoverType={closeTransfer.handoverType}
              isOnlyOneServiceType={isOnlyOneServiceType}
              serviceType={ServiceTypeEnum.ELECTRICITY}
              preferDate={closeTransfer.elecPreferDate}
              onAction={() => onUpdate(ServiceTypeEnum.ELECTRICITY, "action")}
              onCancel={() => onUpdate(ServiceTypeEnum.ELECTRICITY, "cancel")}
            />
          ) : (
            <Stack>
              {!isOnlyOneServiceType && (
                <FormFieldLayout>
                  <Stack space="large">
                    <Heading variant="h4">Electricity</Heading>
                    {!isElectricityEnable && (
                      <Alert tone="info">
                        Account is not ready to be closed.
                      </Alert>
                    )}
                    <Checkbox
                      label={`I want to ${serviceTypeLabel} this account`}
                      id="close-transfer-electricity"
                      checked={closeTransfer.serviceTypes.includes(
                        ServiceTypeEnum.ELECTRICITY
                      )}
                      onChange={(event) =>
                        onChangeServiceType(event, ServiceTypeEnum.ELECTRICITY)
                      }
                      helperText={
                        isElectricityEnable
                          ? null
                          : `Connection in progress, account can't be ${serviceTypeLabelPassive}`
                      }
                      disabled={
                        !isElectricityEnable || hasViewAllRequestsPermissions
                      }
                      error={Boolean(errors.closeTransfer?.serviceTypes)}
                    />
                  </Stack>
                </FormFieldLayout>
              )}
              <FormFieldLayout
                text={`We'll book the ${serviceTypeLabel} as close as possible to the preferred date or at the next available date. Meter type, weather conditions and public holidays can impact time frames`}
              >
                <DatePickerField
                  name="closeTransfer.elecPreferDate"
                  label="Preferred date (on or after)"
                  shouldDisableDate={isUnavailableDateForFuture}
                  disabled={
                    !isElectricityEnable || hasViewAllRequestsPermissions
                  }
                  helperText={
                    isElectricityEnable
                      ? undefined
                      : `Connection in progress, account can't be ${serviceTypeLabelPassive}`
                  }
                />
              </FormFieldLayout>
            </Stack>
          )}

          {/* End Close */}

          {/* Preferred date */}

          {/* End Preferred date */}
        </Stack>
      )}
      {/* End Service */}

      {/* Service */}
      {hasGasSection && (
        <Stack
          space={{
            xs: "large",
            md: "xlarge",
          }}
        >
          {isGasReadOnly ? (
            <CTReadOnlyServiceSection
              handoverType={closeTransfer.handoverType}
              isOnlyOneServiceType={isOnlyOneServiceType}
              serviceType={ServiceTypeEnum.GAS}
              preferDate={closeTransfer.gasPreferDate}
              onAction={() => onUpdate(ServiceTypeEnum.GAS, "action")}
              onCancel={() => onUpdate(ServiceTypeEnum.GAS, "cancel")}
            />
          ) : (
            <Stack>
              {!isOnlyOneServiceType && (
                <FormFieldLayout>
                  <Stack space="large">
                    <Heading variant="h4">Gas</Heading>
                    {!isGasEnable && (
                      <Alert tone="info">
                        Account is not ready to be closed.
                      </Alert>
                    )}
                    <Checkbox
                      label={`I want to ${serviceTypeLabel} this account`}
                      id="close-transfer-gas"
                      checked={values.closeTransfer.serviceTypes.includes(
                        ServiceTypeEnum.GAS
                      )}
                      onChange={(event) =>
                        onChangeServiceType(event, ServiceTypeEnum.GAS)
                      }
                      helperText={
                        isGasEnable
                          ? undefined
                          : `Connection in progress, account can't be ${serviceTypeLabelPassive}`
                      }
                      disabled={!isGasEnable || hasViewAllRequestsPermissions}
                      error={Boolean(errors.closeTransfer?.serviceTypes)}
                    />
                  </Stack>
                </FormFieldLayout>
              )}
              {/* End Close */}
              {/* Preferred date */}
              <FormFieldLayout
                text={`We'll book the ${serviceTypeLabel} as close as possible to the preferred date or at the next available date. Meter type, weather conditions and public holidays can impact time frames`}
              >
                <DatePickerField
                  name="closeTransfer.gasPreferDate"
                  label="Preferred date (on or after)"
                  shouldDisableDate={isUnavailableDateForFuture}
                  disabled={!isGasEnable || hasViewAllRequestsPermissions}
                  helperText={
                    isGasEnable
                      ? undefined
                      : `Connection in progress, account can't be ${serviceTypeLabelPassive}`
                  }
                />
              </FormFieldLayout>
              {isElectricityEnable &&
                isGasEnable &&
                errors.closeTransfer?.serviceTypes && (
                  <FormFieldLayout>
                    <Alert tone="critical">
                      Please specify the fuel for which the close/transfer is
                      required
                    </Alert>
                  </FormFieldLayout>
                )}
              {/* End Preferred date */}
            </Stack>
          )}
        </Stack>
      )}
      {/* End Service */}
    </Stack>
  );
};
